/**
 * The two panes the ledger draws, described as grid structure.
 *
 * These are the single source of truth for both halves of the screen: the cursor navigates this
 * shape and the tables render from it. Building the structure twice -- once for keyboard movement
 * and once in JSX -- is how a grid ends up with an arrow key that lands on a cell that is not
 * where it was drawn, and there is no way to notice that from either side alone.
 *
 * Each row also carries the domain object it was built from, so the table components read
 * `row.category` or `row.transaction` rather than indexing back into the data by position.
 */

export const PANE_BUDGET = 0;
export const PANE_RECORDINGS = 1;

/** Rows numbered in the order they are drawn, head through foot, so `row.index` is the cursor's. */
function buildPane(key, rows) {
	rows.forEach((row, index) => {
		row.index = index;
	});
	return { key, rows };
}

function cells(...list) {
	return list.map((cell) => (typeof cell === 'string' ? { key: cell } : cell));
}

/**
 * The budget half -- the spreadsheet's A3:D34.
 *
 * Only Budgetted is editable, and only for a single month. Spent and Remain are sums of what the
 * recordings say, so a typed value there would be a number the ledger immediately contradicts;
 * across several months Budgetted is a sum too, and there is no one budget row to write it back
 * to. Category names are left alone deliberately: they are the sheet's spine, shared with every
 * other screen in the app, and this is not the place to rename one.
 *
 * @param groups {Array} from `budgetBlockRows()`
 * @param totals {Object} from `budgetBlockRows()`
 * @param budgetEditable {boolean} false when the selection is more than one month
 */
export function budgetPane({ groups, totals }, budgetEditable = true) {
	const rows = [
		{ key: 'head', section: 'head', kind: 'header', cells: cells('category', 'budgeted', 'spent', 'remaining') },
	];

	groups.forEach(({ categoryGroup, rows: categoryRows }) => {
		rows.push({
			key: `group-${categoryGroup.id}`,
			section: 'body',
			kind: 'group',
			categoryGroup,
			cells: cells({ key: 'name', span: 4 }),
		});
		categoryRows.forEach((categoryRow) => {
			// A row spending its parent's budget draws an arrow rather than a number in two of its
			// three columns, so those two carry no amount: the status bar sums what is on screen,
			// and a zero nobody can see making a selected total smaller is worse than no total.
			const shown = (amount) => (categoryRow.usesParentBudget ? null : amount);
			rows.push({
				key: `category-${categoryRow.category.id}`,
				section: 'body',
				kind: 'category',
				...categoryRow,
				cells: cells(
					'category',
					{ key: 'budgeted', editable: budgetEditable, amount: shown(categoryRow.budgeted) },
					{ key: 'spent', amount: categoryRow.spent },
					{ key: 'remaining', amount: shown(categoryRow.remaining) },
				),
			});
		});
	});

	rows.push({
		key: 'total',
		section: 'foot',
		kind: 'total',
		totals,
		cells: cells(
			'label',
			{ key: 'budgeted', amount: totals.budgeted },
			{ key: 'spent', amount: totals.spent },
			{ key: 'remaining', amount: totals.remaining },
		),
	});

	return buildPane('budget', rows);
}

/**
 * The recordings half -- the spreadsheet's E2:L18, plus the blank row at the bottom.
 *
 * The blank row is what makes this an entry surface rather than a report: there is always one, it
 * is where Tab off the end of the last row goes, and committing anything into it submits a new
 * transaction and grows a fresh blank beneath. That is the loop her sheet has, and it is the only
 * reason to have rebuilt the sheet rather than pointed her at the Transactions tab.
 *
 * @param transactions {Array} from `recordingRows()`, already in the sheet's order
 * @param total {number} cents
 * @param draft {Transaction} the unsaved row at the bottom, so a half-typed one survives a redraw
 */
export function recordingsPane({ transactions, total, draft }) {
	const entryCells = (transaction) => cells(
		{ key: 'where', editable: true },
		{ key: 'transaction_date', editable: true },
		// The amount is repeated here, on the cell, because that is where the selection reads it
		// from: the status bar sums cells, and it has no way back to a transaction from one.
		{ key: 'amount', editable: true, amount: transaction.amount },
		{ key: 'category_id', editable: true },
	);

	const rows = [
		{ key: 'head', section: 'head', kind: 'header', cells: cells('where', 'transaction_date', 'amount', 'category_id') },
	];

	transactions.forEach((transaction) => {
		rows.push({
			// A transaction the server has never seen has neither an id nor a submit id yet, and
			// `getRenderKey()` has nothing to answer with; the instance number is what tells two
			// of those apart.
			key: `transaction-${transaction.getRenderKey() || `instance-${transaction.instanceNumber}`}`,
			section: 'body',
			kind: 'transaction',
			transaction,
			cells: entryCells(transaction),
		});
	});

	rows.push({ key: 'new', section: 'body', kind: 'new', transaction: draft, cells: entryCells(draft) });

	rows.push({
		key: 'total',
		section: 'foot',
		kind: 'total',
		total,
		cells: cells({ key: 'label', span: 2 }, { key: 'amount', amount: total }, 'category_id'),
	});

	return buildPane('recordings', rows);
}
